import { Button } from '@shared/ui';
import { useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import type { RootState } from '../../../store/store';
import { useAuth } from '../AuthProvider';
import { hasAnyPermission } from '../permissions';

type AdminRouteProps = {
  children: React.ReactNode;
  requiredPermissions: string[];
};

const hasAdminAccess = (user: RootState['auth']['user'] | null, requiredPermissions: string[]) => {
  if (!user) return false;
  const permissions = user.permissions || [];
  const roles = user.roles || [];
  return hasAnyPermission(permissions, requiredPermissions, roles);
};

export default function AdminRoute({
  children,
  requiredPermissions,
}: AdminRouteProps): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, token, status, isUserResolved, user, impersonation } = useSelector(
    (s: RootState) => s.auth
  );
  const { isExchanging } = useAuth();
  const isUserLoading = isAuthenticated && (!isUserResolved || !user);

  // If impersonating, redirect away from admin pages
  // Impersonated users should not have access to admin functionality
  if (impersonation?.active) {
    return <Navigate to="/" replace />;
  }

  // Show loading state while auth/user data is being fetched
  if (isExchanging || isUserLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          fontSize: '14px',
          color: '#666',
        }}
      >
        Verifying permissions...
      </div>
    );
  }

  if (!isAuthenticated || !token || status === 'expired') {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (!hasAdminAccess(user, requiredPermissions)) {
    return (
      <div
        style={{
          maxWidth: '600px',
          margin: '100px auto',
          padding: '32px',
          textAlign: 'center',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <h1 style={{ fontSize: '24px', marginBottom: '16px', color: '#d32f2f' }}>Access Denied</h1>
        <p style={{ fontSize: '16px', color: '#666', marginBottom: '24px' }}>
          You do not have permission to access this page. This page requires admin privileges.
        </p>
        <p style={{ fontSize: '14px', color: '#999', marginBottom: '24px' }}>
          If you believe you should have access, please contact your administrator.
        </p>
        <Button variant="contained" onClick={() => navigate('/members')}>
          Return to Home
        </Button>
      </div>
    );
  }

  return <>{children}</>;
}
